import type {
  CreditBillingTimelineEntry,
  CreditBillingTimelineEntryStatus,
  CreditBillingTimelineSummary,
} from '@nadoutong/contracts';
import { DualCurrencyAmount } from '../DualCurrencyAmount.tsx';
import { usdMicrosToCnyCents } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Chip,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';

/**
 * Multi-month billing timeline (router#2010, epic #1997 E4).
 *
 * Props are locked for E5: the credit monitor expands a customer onto
 * this same component. Do not fork a simplified row set.
 */

export interface CreditBillingTimelineProps {
  readonly summary: CreditBillingTimelineSummary;
  readonly entries: readonly CreditBillingTimelineEntry[];
  readonly cnyPerUsd?: number | null;
  readonly testId?: string;
}

const STATUS_LABEL: Record<CreditBillingTimelineEntryStatus, I18nKey> = {
  paid_on_time: 'credit.overview.status_chip.paid_on_time',
  paid_late_le_7: 'credit.overview.status_chip.paid_late',
  paid_late_gt_7: 'credit.overview.status_chip.paid_late',
  overdue_unpaid: 'credit.overview.status_chip.overdue_unpaid',
  not_due: 'credit.overview.status_chip.not_due',
  no_payment_due: 'credit.overview.status_chip.no_payment_due',
  draw: 'credit.overview.status_chip.draw',
};

const STATUS_TONE: Record<CreditBillingTimelineEntryStatus, ChipTone> = {
  paid_on_time: 'ok',
  paid_late_le_7: 'warn',
  paid_late_gt_7: 'bad',
  overdue_unpaid: 'bad',
  not_due: 'neutral',
  no_payment_due: 'neutral',
  draw: 'neutral',
};

const AMOUNT_BAR_TONE: Record<CreditBillingTimelineEntryStatus, string> = {
  paid_on_time: 'bg-ok',
  paid_late_le_7: 'bg-warn',
  paid_late_gt_7: 'bg-bad',
  overdue_unpaid: 'bg-bad',
  not_due: 'bg-line',
  no_payment_due: 'bg-line',
  draw: 'bg-warn',
};

export function CreditPaymentSummaryLine({
  summary,
  cnyPerUsd = null,
  display = 'dual',
  testId,
}: {
  readonly summary: CreditBillingTimelineSummary;
  readonly cnyPerUsd?: number | null;
  readonly display?: 'dual' | 'cny';
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  const cnyCents = usdMicrosToCnyCents(summary.current_unpaid_usd_micros, cnyPerUsd);
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-dense text-paper-mid"
      data-testid={testId}
    >
      <span>
        {t('credit.overview.timeline.summary', {
          months: String(summary.window_months),
          onTime: String(summary.on_time_count),
          closed: String(summary.closed_count),
          maxLate: String(summary.max_overdue_days),
          tmp: String(summary.temporary_draw_count),
        })}
      </span>
      {display === 'cny' ? (
        <DualCurrencyAmount display="cny" cnyCents={cnyCents} signed={false} align="start" />
      ) : (
        <DualCurrencyAmount
          usdMicros={summary.current_unpaid_usd_micros}
          cnyCents={cnyCents}
          signed={false}
          align="start"
        />
      )}
    </div>
  );
}

export function CreditBillingTimeline({
  summary,
  entries,
  cnyPerUsd = null,
  testId = 'credit-billing-timeline',
}: CreditBillingTimelineProps): JSX.Element {
  const t = useT();
  let maxBilled = 0;
  for (const entry of entries) {
    if (entry.billed_usd_micros > maxBilled) {
      maxBilled = entry.billed_usd_micros;
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <CreditPaymentSummaryLine
        summary={summary}
        cnyPerUsd={cnyPerUsd}
        display="cny"
        testId={`${testId}-summary`}
      />
      <Table testId={`${testId}-table`} className="rounded-none border-0">
        <THead>
          <Tr interactive={false}>
            <Th>{t('credit.overview.timeline.col.period')}</Th>
            <Th>{t('credit.overview.timeline.col.bar')}</Th>
            <Th num>{t('credit.overview.timeline.col.amount')}</Th>
            <Th>{t('credit.overview.timeline.col.due')}</Th>
            <Th>{t('credit.overview.timeline.col.paid')}</Th>
            <Th num>{t('credit.overview.timeline.col.overdue_days')}</Th>
            <Th>{t('credit.overview.timeline.col.status')}</Th>
          </Tr>
        </THead>
        <TBody>
          {entries.length === 0 ? (
            <TableEmpty colSpan={7} testId={`${testId}-empty`}>
              {t('credit.overview.timeline.empty')}
            </TableEmpty>
          ) : (
            entries.map((entry) => {
              const widthPct =
                maxBilled > 0 ? (entry.billed_usd_micros / maxBilled) * 100 : 0;
              return (
                <Tr
                  key={entry.id}
                  interactive={false}
                  data-testid={`${testId}-row-${entry.id}`}
                  data-kind={entry.kind}
                  data-statement-kind={entry.statement_kind ?? ''}
                  data-status={entry.status}
                  data-overdue-days={String(entry.overdue_days)}
                >
                  <Td mono>
                    <div className="flex flex-col gap-0.5">
                      <span>{entry.period}</span>
                      {entry.kind === 'temporary_draw' ? (
                        <Chip
                          tone="warn"
                          lamp={false}
                          testId={`${testId}-kind-${entry.id}`}
                        >
                          {t('credit.overview.timeline.kind.temporary')}
                        </Chip>
                      ) : null}
                      {entry.statement_kind === 'one_shot' ? (
                        <Chip
                          tone="warn"
                          lamp={false}
                          testId={`${testId}-kind-${entry.id}`}
                        >
                          {t('credit.overview.timeline.kind.one_shot')}
                        </Chip>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    <div className="h-3.5 w-full rounded-sm bg-well">
                      <div
                        className={cx('h-3.5 rounded-sm', AMOUNT_BAR_TONE[entry.status])}
                        style={{ width: `${widthPct}%` }}
                        data-testid={`${testId}-bar-${entry.id}`}
                      />
                    </div>
                  </Td>
                  <Td num emphasis>
                    <span data-testid={`${testId}-amount-${entry.id}`}>
                      {entry.kind === 'temporary_draw' ? (
                        <span className="flex flex-col items-end gap-0.5">
                          <span>{t('credit.overview.layer.limit', { limit: '' }).trim()}</span>
                          <DualCurrencyAmount
                            display="cny"
                            cnyCents={usdMicrosToCnyCents(entry.billed_usd_micros, cnyPerUsd)}
                            signed={false}
                          />
                        </span>
                      ) : (
                        <DualCurrencyAmount
                          display="cny"
                          cnyCents={usdMicrosToCnyCents(entry.billed_usd_micros, cnyPerUsd)}
                          signed={false}
                        />
                      )}
                    </span>
                  </Td>
                  <Td mono>{entry.due_date ?? t('credit.overview.dash')}</Td>
                  <Td mono data-testid={`${testId}-paid-${entry.id}`}>
                    {entry.paid_at ?? t('credit.overview.dash')}
                  </Td>
                  <Td num data-testid={`${testId}-overdue-${entry.id}`}>
                    {String(entry.overdue_days)}
                  </Td>
                  <Td>
                    <Chip
                      tone={STATUS_TONE[entry.status]}
                      testId={`${testId}-status-${entry.id}`}
                    >
                      {t(STATUS_LABEL[entry.status], { days: String(entry.overdue_days) })}
                    </Chip>
                  </Td>
                </Tr>
              );
            })
          )}
        </TBody>
      </Table>
    </div>
  );
}
